import { ServiceClient, type InterceptFn, type TokenSource } from '@compassdigital/sdk.typescript';
import { useAuthStore } from '@centric-os/stores/auth/use-auth-store';
import { useLoadingStore } from '@centric-os/stores/loading/use-loading-store';

/**
 * Creates a token source that resolves the current auth token on each request.
 */
function createTokenSource(): TokenSource {
  return () => useAuthStore().getValidAccessToken();
}

/**
 * Creates an intercept function that toggles the global loading state.
 */
function createIntercept(): InterceptFn {
  return async (req, fetch) => {
    const loadingStore = useLoadingStore();
    loadingStore.loading = true;
    try {
      return await fetch(req);
    } finally {
      loadingStore.loading = false;
    }
  };
}

/**
 * Creates a ServiceClient configured with auth-token injection and loading-state toggling.
 *
 * `baseUrl` overrides the CDL API base for this client instance. Pass a string
 * to move every service, or a `{ service: url }` map to move some — the SDK
 * resolves the base per service and modules use the map form to point one
 * service at a locally running backend.
 *
 * Note on the map form: the SDK has no wildcard entry. A service absent from
 * the map falls through to its `stage` default and throws when no stage is
 * set, so a map has to name every service the caller uses.
 */
export function createServiceClient(options?: {
  baseUrl?: string | Record<string, string>;
}): ServiceClient {
  return new ServiceClient({
    base_url: options?.baseUrl || import.meta.env.VITE_CDL_ENDPOINT_BASE_URL,
    token: createTokenSource(),
    intercept: createIntercept(),
  });
}
